import { log } from '@/log'
import { parseSessionPayload, SessionPayload } from '@/services/session/session-state'

const namespace = 'session-state-store'

interface SessionStateMirror {
  session: SessionPayload | null
}

export const SESSION_STATE_MIRROR_HEADER = 'X-Session-State'

/**
 * セッション状態のメモリstore。
 *
 * 状態の真はserver側にあり、serverはセッションcookie（mc_s）を発行・削除した応答
 * （およびセッション前提の処理がcookie不在を観測した応答）に、X-Session-Stateミラーヘッダで
 * その時点の最終状態を載せる。clientはそれを本storeへ取り込んで参照し、document.cookieは読まない
 * （bot・一部WebViewではdocument.cookieが機能せず、読み取りの成否に正しさを依存できない。issue #9104）。
 *
 * - staleでありうる（server側で失効していても次のミラーまで気づかない）が、
 *   保持している値そのものがserverの通知と食い違うことはない
 * - 「未受信（unknown）」と「セッション無し」は区別する。未受信を不在と解釈してはならない
 */
export class SessionStateStore {
  static getInstance(): SessionStateStore {
    if (!SessionStateStore.instance) {
      SessionStateStore.instance = new SessionStateStore()
    }

    return SessionStateStore.instance
  }

  private static instance: SessionStateStore

  private mirror: SessionStateMirror | null = null

  /**
   * ミラーヘッダ値（base64 JSON）を取り込み、storeを丸ごとその値に置き換える。
   * パースできない値は無視して現状を維持する
   * （壊れたミラーで状態を失うより、staleのまま次のミラーを待つ方が安全）。
   */
  updateFromMirrorHeader(headerValue: string): void {
    const mirror = this.parseMirror(headerValue)
    if (!mirror) {
      return
    }

    this.mirror = mirror
    log.debug({ service: namespace, mirror })
  }

  /**
   * 最後に通知されたセッションpayload。
   * 「セッション無し」と「未受信」はいずれもnull（区別が必要ならsessionIsAbsent/hasReceivedMirror）
   */
  readSession(): SessionPayload | null {
    return this.mirror === null ? null : this.mirror.session
  }

  /**
   * serverが「有効なセッションが無い」と明示的に通知した状態かどうか。未受信（unknown）ではfalse
   */
  sessionIsAbsent(): boolean {
    return this.mirror !== null && this.mirror.session === null
  }

  hasReceivedMirror(): boolean {
    return this.mirror !== null
  }

  private parseMirror(headerValue: string): SessionStateMirror | null {
    try {
      const raw = JSON.parse(atob(headerValue))
      if (raw === null || typeof raw !== 'object' || !('session' in raw)) {
        log.error(`${namespace}: invalid state mirror header`, raw)

        return null
      }

      if (raw.session === null) {
        return { session: null }
      }

      const session = parseSessionPayload(raw.session)
      if (!session) {
        return null
      }

      return { session }
    } catch (error) {
      log.error(`${namespace}: failed to parse the state mirror header`, error)

      return null
    }
  }
}
